import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NewsletterService } from '../../services/newsletter.service';
import { NotAvailableTipDirective } from '@shared/directives/not-available-tip.directive';
import { LandingStore } from '../../../features/landing-page/data/store';

/**
 * A footer link, resolved for the template: in-app links go through the
 * router (no full page reload, which briefly showed the footer on its own),
 * anything else is a plain external href.
 */
export interface FooterLink {
  label: string;
  /** Router path for in-app links. */
  path?: string;
  fragment?: string;
  /** Full URL for external links. */
  href?: string;
}

/** "/", "#plans", "/#plans" → router link; "https://…" etc. → external href. */
function toFooterLink(link: { label: string; url: string }): FooterLink {
  const url = link.url.trim();
  if (!url.startsWith('/') && !url.startsWith('#')) {
    return { label: link.label, href: url };
  }
  const [path, fragment] = url.split('#');
  return { label: link.label, path: path || '/', fragment: fragment || undefined };
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, NotAvailableTipDirective],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css'
})
export class FooterComponent {
  private readonly fb = inject(FormBuilder);
  private readonly newsletterService = inject(NewsletterService);
  private readonly landingStore = inject(LandingStore);

  // Form
  subscribeForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  // State from newsletter service
  isLoading = this.newsletterService.isLoading;
  error = this.newsletterService.error;
  success = this.newsletterService.success;
  alreadySubscribed = this.newsletterService.alreadySubscribed;

  // State from landing store
  readonly footerSection = this.landingStore.footer;

  // Local state
  formSubmitted = signal(false);

  // Footer data comes from the API only; a missing value hides its element.
  about = computed(() => this.footerSection()?.about || '');

  address = computed(() => this.footerSection()?.address || '');
  email = computed(() => this.footerSection()?.email || '');
  phone = computed(() => this.footerSection()?.phone || '');
  workingHours = computed(() => this.footerSection()?.workingHours || '');
  copyright = computed(() => this.footerSection()?.copyright || '');

  quickLinks = computed(() => (this.footerSection()?.quickLinks ?? []).map(toFooterLink));

  socialLinks = computed(() => this.footerSection()?.socialLinks ?? []);

  /**
   * The design's social icons, shown while the API sends no links. They carry
   * no URL: a click shows a small "not available yet" tip.
   */
  readonly socialIcons = [
    { label: 'Email', icon: 'assets/footer/icon-email.svg' },
    { label: 'WhatsApp', icon: 'assets/footer/icon-whatsapp.svg' },
    { label: 'LinkedIn', icon: 'assets/footer/icon-linkedin.svg' },
    { label: 'Facebook', icon: 'assets/footer/icon-facebook.svg' },
  ];

  /**
   * This page's own sections, listed while the API sends no quick links.
   * They are in-page navigation, not backend data, so they work as-is.
   */
  readonly pageLinks: FooterLink[] = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/', fragment: 'services' },
    { label: 'Event Highlights', path: '/', fragment: 'events' },
    { label: 'Our Plans', path: '/', fragment: 'plans' },
  ];

  // Contact info computed
  hasContactInfo = computed(() => {
    return this.address() || this.email() || this.phone();
  });

  onSubmit(): void {
    this.formSubmitted.set(true);

    if (this.subscribeForm.invalid) {
      return;
    }

    const email = this.subscribeForm.value.email;
    this.newsletterService.subscribe(email).subscribe(response => {
      if (response?.success) {
        this.subscribeForm.reset();
        this.formSubmitted.set(false);
      }
    });
  }

  getEmailError(): string | null {
    const control = this.subscribeForm.get('email');
    if (!control || !control.errors || (!control.touched && !this.formSubmitted())) {
      return null;
    }

    if (control.errors['email']) {
      return 'Please enter a valid email';
    }

    return null;
  }

  hasEmailError(): boolean {
    return this.getEmailError() !== null;
  }

  dismissSuccess(): void {
    this.newsletterService.reset();
  }

  getSocialIcon(link: { label: string; url: string; icon: string }): string {
    if (link.icon) return link.icon;

    const label = link.label.toLowerCase();
    if (label.includes('email')) return 'assets/footer/icon-email.svg';
    if (label.includes('whatsapp')) return 'assets/footer/icon-whatsapp.svg';
    if (label.includes('linkedin')) return 'assets/footer/icon-linkedin.svg';
    if (label.includes('facebook')) return 'assets/footer/icon-facebook.svg';
    if (label.includes('instagram')) return 'assets/footer/icon-instagram.svg';
    if (label.includes('twitter') || label.includes('x')) return 'assets/footer/icon-twitter.svg';

    return 'assets/footer/icon-link.svg';
  }
}
